// src/components/Admin/EvaluationRutasComp/TeacherEvaluationGrid.jsx
import React, { useState, useEffect } from "react";
import {
  Loader,
  Icon,
  Modal,
  Header,
  Button,
  Form,
  Radio,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Image,
} from "semantic-ui-react";
import { 
  RutasPanel,
  ScenesPanel,
  SceneIframePanel,
  EvaluationStudents,
} from "./RutasEvaluationList";
import { QuizApi } from "../../../api/quiz";
import { StudentAPI } from "../../../api/studentAPI";
import "./TeacherEvaluationGrid.scss";

const quizApi = new QuizApi();
const studentAPI = new StudentAPI();

export function TeacherEvaluationGrid({ teacher, accessToken }) {
  // → Estados de rutas/escenas
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [selectedClassNumber, setSelectedClassNumber] = useState(null);

  // → Estados para el iframe
  const [iframeShortLink, setIframeShortLink] = useState(null);
  const [iframeFallbackUrl, setIframeFallbackUrl] = useState(null);
  const [sceneData, setSceneData] = useState(null);

  // → Estados para el modal de Quiz
  const [quizzesList, setQuizzesList] = useState([]);
  const [loadingQuizzes, setLoadingQuizzes] = useState(false);
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [selectedSceneForQuiz, setSelectedSceneForQuiz] = useState(null);
  const [selectedQuizId, setSelectedQuizId] = useState(null);

  // → Estados para RankingQuiz
  const [activeQuizId, setActiveQuizId] = useState(null);
  const [rankingQuizName, setRankingQuizName] = useState("");
  const [showRankingModal, setShowRankingModal] = useState(false);
  const [loadingRanking, setLoadingRanking] = useState(false);
  const [rankingData, setRankingData] = useState([]);

  // 1️⃣ Al montar, traemos todos los quizzes del teacher
  useEffect(() => {
    if (!teacher?._id) return;
    (async () => {
      setLoadingQuizzes(true);
      try {
        const { quizzes } = await quizApi.getAllQuizzes(
          accessToken,
          teacher._id
        );
        setQuizzesList(quizzes);
      } catch (err) {
        console.error("Error cargando quizzes:", err);
      } finally {
        setLoadingQuizzes(false);
      }
    })();
  }, [teacher, accessToken]);

  // 2️⃣ Handler para “Entrar” en una escena
  const handleEnterScene = (scene) => {
    if (!quizzesList.length) {
      openScene(scene);
      setActiveQuizId(null);
      setRankingQuizName("");
    } else {
      setSelectedSceneForQuiz(scene);
      setSelectedQuizId(null);
      setShowQuizModal(true);
    }
  };

  // 3️⃣ Abrir la escena en el iframe
  const openScene = (scene) => {
    setSceneData(scene);
    if (scene.shortLink && scene.shortLink !== "No shortLink") {
      setIframeShortLink(scene.shortLink);
      setIframeFallbackUrl(null);
    } else {
      setIframeShortLink(null);
      setIframeFallbackUrl(scene.urlsala);
    }
  };

  // 4️⃣ Confirmar asignación de quiz y luego entrar
  const handleConfirmQuiz = async () => {
    try {
      await quizApi.setActiveTeacherQuiz(
        accessToken,
        teacher._id,
        selectedQuizId
      );
      setActiveQuizId(selectedQuizId);
      const quizObj = quizzesList.find(q => q._id === selectedQuizId);
      setRankingQuizName(quizObj?.name || "");
    } catch (err) {
      console.error("Error asignando quiz activo:", err);
    }
    setShowQuizModal(false);
    openScene(selectedSceneForQuiz);
  };

  // 5️⃣ Entrar sin asignar test
  const handleSkipQuiz = async () => {
    try {
      await quizApi.setActiveTeacherQuiz(accessToken, teacher._id, null);
      setActiveQuizId(null);
      setRankingQuizName("");
    } catch (err) {
      console.error("Error desasignando quiz activo:", err);
    }
    setShowQuizModal(false);
    openScene(selectedSceneForQuiz);
  };

  // 6️⃣ Handler para boton RankingQuiz: abre modal y llama API y estudiantes
  const handleRankingClick = async (quizId) => {
    setShowRankingModal(true);
    setLoadingRanking(true);
    try {
      // Obtener nombre del quiz
      const quizObj = quizzesList.find(q => q._id === quizId);
      setRankingQuizName(quizObj?.name || "");
      // Obtener ranking
      const result = await quizApi.getRankingResults(
        accessToken,
        teacher._id,
        quizId,
        selectedClassNumber
      );
      const ranking = result.ranking || [];

      // Obtener lista de estudiantes
      const studentsRes = await studentAPI.getStudentsByClassnumber(
        accessToken,
        teacher.institution._id,
        selectedClassNumber
      );
      const studentsList = studentsRes.students || [];

      // Enriquecer ranking con avatar
      const enriched = ranking.map((item) => {
        const student = studentsList.find((s) => s._id === item.studentId);
        return {
          ...item,
          avatar: student?.avatar,
        };
      });
      setRankingData(enriched);
    } catch (err) {
      console.error("Error obteniendo ranking o estudiantes:", err);
      setRankingData([]);
    } finally {
      setLoadingRanking(false);
    }
  };

  return (
    <div className="teacher-evaluation-grid">
      {/* === IZQUIERDA: rutas, escenas, iframe === */}
      <div className="teacher-evaluation-grid__left">
        <RutasPanel
          teacher={teacher._id}
          accessToken={accessToken}
          onSelectRoute={({ routeId, classnumber }) => {
            setSelectedRouteId(routeId);
            setSelectedClassNumber(classnumber);
            setIframeShortLink(null);
            setIframeFallbackUrl(null);
            setSceneData(null);
            setActiveQuizId(null);
            setRankingQuizName("");
          }}
        />

        <ScenesPanel
          routeId={selectedRouteId}
          accessToken={accessToken}
          onEnterScene={handleEnterScene}
        />
 
        <SceneIframePanel
          shortLink={iframeShortLink}
          fallbackUrl={iframeFallbackUrl}
          accessToken={accessToken}
          routeId={selectedRouteId}
          scene={sceneData}
        />
      </div>

      {/* === DERECHA: estudiantes/asistentes + Ranking === */}
      <div className="teacher-evaluation-grid__right">
        <EvaluationStudents
          teacherInstitutionId={teacher.institution._id}
          classNumber={selectedClassNumber}
          accessToken={accessToken}
          route={selectedRouteId}
          activeQuizId={activeQuizId}
          onRankingClick={handleRankingClick}
        />
      </div>

      {/* === MODAL PARA SELECCIÓN DE QUIZ === */}
      <Modal
        className="quiz-modal"
        dimmer="blurring"
        open={showQuizModal}
        size="small"
      >
        <Header icon="tasks" content="Selecciona un Test para esta escena (opcional)" />
        <Modal.Content>
          {loadingQuizzes ? (
            <Loader active inline="centered" />
          ) : (
            <Form>
              {quizzesList.map((q) => (
                <Form.Field key={q._id}>
                  <Radio
                    label={q.name}
                    name="quizRadio"
                    value={q._id}
                    checked={selectedQuizId === q._id}
                    onChange={(_, { value }) => setSelectedQuizId(value)}
                  />
                </Form.Field>
              ))}
            </Form>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button color="grey" inverted onClick={handleSkipQuiz}>
            <Icon name="times" /> Entrar sin Test
          </Button>
          <Button
            color="grey" inverted onClick={handleConfirmQuiz}
            disabled={!selectedQuizId}
          >
            <Icon name="check" /> Asignar y Entrar
          </Button>
        </Modal.Actions>
      </Modal>

      {/* === MODAL PARA RANKING DE QUIZ === */}
      <Modal
        className="ranking-modal"
        dimmer="blurring"
        open={showRankingModal}
        onClose={() => setShowRankingModal(false)}
        size="small"
      >
        <Header icon="chart bar" content={`Ranking del Quiz: ${rankingQuizName}`} />
        <Modal.Content>
          {loadingRanking ? (
            <Loader active inline="centered">Cargando ranking...</Loader>
          ) : (
            <Table basic='very' celled>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>#</TableHeaderCell>
                  <TableHeaderCell>Alumno</TableHeaderCell>
                  <TableHeaderCell>Correctas</TableHeaderCell>
                  <TableHeaderCell>Incorrectas</TableHeaderCell>
                  <TableHeaderCell>Tiempo (s)</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rankingData.map((item, idx) => (
                  <TableRow key={item.studentId}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>
                      <Header as='h4' image>
                        {item.avatar ? (
                          <Image src={item.avatar} rounded size='mini' />
                        ) : (
                          <Icon name='user circle' size='mini' />
                        )}
                        <Header.Content>
                          {item.studentName}
                          <Header.Subheader>{selectedClassNumber}</Header.Subheader>
                        </Header.Content>
                      </Header>
                    </TableCell>
                    <TableCell>{item.totalCorrect}</TableCell>
                    <TableCell>{item.totalIncorrect}</TableCell>
                    <TableCell>{item.timeTaken}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button color="blue" inverted onClick={() => setShowRankingModal(false)}>
            <Icon name="close" /> Cerrar
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
}
